// The `Game` object over a `GameStore` (PLAN-CLIENT-SPLIT §2).
//
// **One subscription to the store, fanned out as typed events; one memo of
// the facts, keyed on the viewed board.** The store hands out a whole snapshot
// on every change and knows no event names, so this is where "what changed"
// is worked out (`turnDelta`) and where "what is there" is built once rather
// than by every zone that asks (`boardFacts`).
//
// Day 2 lands `board` and the `turn` event; days 5–6 the `session`; `catalog`
// is the rules' tables and is shared by every game; day 7 mounts the
// `drivers`. `play` and `test` follow and land on this same object.

import type {
  Board,
  BoardFacts,
  Events,
  Game,
  HistoryFacts,
  Intent,
  Outcome,
  Square,
  SquareFacts,
  Status,
  DevHandle
} from "src/api";
import { createDrivers, type EngineDrivers } from "src/engine/drivers";
import type { OpponentDriverOptions } from "src/engine/drivers/opponent";
import { DEV_TOOLS } from "src/engine/game/debugHook";
import { type GameSnapshot, GameStore } from "src/engine/game/GameStore";
import type { RoomClient } from "src/engine/net/RoomClient";
import type { Command, GameState } from "src/engine/rules/commands";
import { UNDO_REFUSAL } from "src/engine/session/refusals";
import { createSession, type Session } from "src/engine/session/Session";
import { catalog } from "./catalog";
import { turnDelta } from "./delta";
import { boardFacts, fixtureOf, statusOf } from "./facts";
import { createOpener, type OpenerOptions } from "./open";

/**
 * The engine's side of the `Game`: what the composition root holds and the
 * client is not handed. A room joined later is attached here, so the session
 * sends through it and sits in its seat.
 */
export interface EngineGame extends Game {
  readonly session: Session;
  readonly drivers: EngineDrivers;
  attachRoom(client: RoomClient | null): void;
  /**
   * The role answered: this account may read `Dev`, so `game.dev` is there on
   * a deployment too. The shell calls it once the ability arrives
   * (`useDevTools`); a dev build needs no call.
   */
  allowDev(allowed: boolean): void;
}

/** What a test injects: a planner that needs no worker. */
export interface CreateGameOptions {
  drivers?: OpponentDriverOptions;
  /** The front door's network and level files, for a test. */
  open?: OpenerOptions;
}

type Listeners = { [E in keyof Events]: Set<(e: Events[E]) => void> };

/** The board on screen: the history view's, else the live one. */
const shown = (snap: GameSnapshot): GameState => snap.history.viewed ?? snap.state;

export function createGame(store: GameStore, options: CreateGameOptions = {}): EngineGame {
  const listeners: Listeners = {
    turn: new Set(),
    focus: new Set(),
    cue: new Set(),
    plan: new Set(),
    branch: new Set()
  };
  let prev = store.getSnapshot();
  // The side whose turn the last change played back — `BoardFacts.tempo` is
  // theirs. The side to move until anybody has moved.
  let mover = prev.state.position.turn;
  let memo: { state: GameState; mover: number; facts: BoardFacts } | null = null;

  const emit = <E extends keyof Events>(event: E, e: Events[E]): void => {
    for (const fn of listeners[event]) fn(e);
  };
  const unsubscribe = store.subscribe(() => {
    const next = store.getSnapshot();
    const delta = turnDelta(prev, next);
    if (delta.kind === "move" || delta.kind === "tick") mover = delta.side;
    prev = next;
    emit("turn", delta);
  });
  // The session: what a click means, over the same store, speaking through
  // the same events. It subscribes to the store itself.
  const session = createSession(store, {
    cue: (cue) => emit("cue", cue),
    focus: (focus) => emit("focus", focus)
  });
  // **One road for a command, whoever sends it.** The room when one is
  // attached, else the store — the session's door, so a driver's move and a
  // click are validated and sequenced the same way.
  let room: RoomClient | null = null;
  const dispatch = (command: Command): boolean =>
    room ? room.send(command) : store.dispatch(command);
  const drivers = createDrivers(
    {
      store,
      dispatch,
      emit: {
        focus: (focus) => emit("focus", focus),
        plan: (report) => emit("plan", report),
        branch: (branch) => emit("branch", branch)
      }
    },
    options.drivers
  );

  // Declared before `play` reads it; `attachRoom` is the object's, below.
  const attachRoom = (client: RoomClient | null): void => {
    room = client;
    session.attach(
      client
        ? {
            dispatch: (command) => client.send(command),
            seat: () => {
              const seat = client.getState().seat;
              return seat === "white" ? 0 : seat === "black" ? 1 : null;
            }
          }
        : null
    );
  };
  const opener = createOpener({ store, drivers, session, attachRoom }, options.open);

  const facts = (): BoardFacts => {
    const state = shown(store.getSnapshot());
    if (memo && memo.state === state && memo.mover === mover) return memo.facts;
    memo = { state, mover, facts: boardFacts(state, mover) };
    return memo.facts;
  };

  const status = (): Status => {
    const snap = store.getSnapshot();
    return statusOf(shown(snap), snap.history.view);
  };

  const history = (): HistoryFacts => {
    const { count, view, ahead } = store.getSnapshot().history;
    return { count, step: view, ahead };
  };

  /**
   * The chrome's acts that are not clicks. Undo and a history step are the
   * store's — in a room, undo is a *request* and the server cuts — and each is
   * refused through the same pill a click is.
   */
  const play = (intent: Intent): Outcome => {
    const side = session.core.localSide;
    switch (intent.kind) {
      case "undo": {
        if (room) return room.undo() ? { ok: true } : refuse("Nothing to undo.");
        const why = store.undoRefusal(side);
        if (why !== null) return refuse(UNDO_REFUSAL[why]);
        return store.undo(side) !== null ? { ok: true } : refuse("Nothing to undo.");
      }
      case "history":
        // The selection goes with the step: what was selected was picked on a
        // position the player can no longer see.
        store.setView(intent.step, side);
        session.dismiss();
        return { ok: true };
      // The one below is a command, sent through the session's door so they
      // are bound to the turn and refused through the pill like any click.
      case "pass":
        return sent(session.core.send({ type: "pass" }));
      case "new-game":
        // Not awaited: a level loads after this answers, and a refusal then
        // arrives through the pill (`open`'s own `refused`).
        void opener.open(intent.options);
        return { ok: true };
    }
  };
  /** A command's answer as an `Outcome`: the door already said why not. */
  const sent = (ok: boolean): Outcome =>
    ok ? { ok: true } : { ok: false, refusal: session.core.refused.text ?? "The rules refused that." };
  const refuse = (refusal: string): Outcome => {
    session.core.setRefusal(refusal);
    return { ok: false, refusal };
  };

  // The store itself, for the smokes and the dev menu: on a dev build
  // (`DEV_TOOLS`, `exposeForTests`'s own guard) or once the role says so.
  // Not a security boundary — see `DevHandle`.
  const handle: DevHandle = {
    snapshot: () => store.getSnapshot(),
    dispatch: (command) => store.dispatch(command as Command)
  };
  let devAllowed = DEV_TOOLS;

  const board: Board = {
    facts,
    at: (sq: Square): SquareFacts | undefined => facts().squares.get(sq),
    status,
    history,
    fixture: () => JSON.stringify(fixtureOf(facts(), status()), null, 2)
  };

  return {
    board,
    session,
    catalog,
    drivers,
    play,
    get dev() {
      return devAllowed ? handle : undefined;
    },
    allowDev(allowed) {
      devAllowed = allowed;
    },
    open: opener.open,
    saved: opener.saved,
    attachRoom,
    on(event, fn) {
      const set = listeners[event];
      set.add(fn);
      return () => {
        set.delete(fn);
      };
    },
    dispose() {
      opener.close();
      unsubscribe();
      drivers.dispose();
      session.dispose();
      for (const set of Object.values(listeners)) set.clear();
      memo = null;
    }
  };
}

/**
 * A `Game` over a board that will not change — a report's snapshot, a tuner's
 * frame, a story. Its own store, so the facts memo and the events work as they
 * do for a live game, and nothing is shared with the one being played.
 */
export const gameOf = (state: GameState): Game => createGame(new GameStore(state));
